import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { MapPin, Search, Users, Building, Navigation } from 'lucide-react';

interface LocationData {
  name: string;
  path: string;
  county: string;
  region: string;
  type: 'city' | 'town' | 'coastal' | 'metropolitan';
}

const allLocations: LocationData[] = [
  // Major Cities & Metropolitan Areas
  { name: 'Raleigh', path: '/locations/raleigh', county: 'Wake', region: 'Triangle', type: 'metropolitan' },
  { name: 'Durham', path: '/locations/durham', county: 'Durham', region: 'Triangle', type: 'metropolitan' },
  { name: 'Chapel Hill', path: '/locations/chapel-hill', county: 'Orange', region: 'Triangle', type: 'metropolitan' },
  { name: 'Cary', path: '/locations/cary', county: 'Wake', region: 'Triangle', type: 'metropolitan' },
  { name: 'Apex', path: '/locations/apex', county: 'Wake', region: 'Triangle', type: 'metropolitan' },
  { name: 'Rocky Mount', path: '/locations/rocky-mount', county: 'Nash', region: 'Eastern NC', type: 'city' },
  { name: 'Greenville', path: '/locations/greenville', county: 'Pitt', region: 'Eastern NC', type: 'city' },
  { name: 'Wilson', path: '/locations/wilson', county: 'Wilson', region: 'Eastern NC', type: 'city' },
  { name: 'Goldsboro', path: '/locations/goldsboro', county: 'Wayne', region: 'Eastern NC', type: 'city' },
  { name: 'New Bern', path: '/locations/new-bern', county: 'Craven', region: 'Coastal', type: 'city' },
  { name: 'Kinston', path: '/locations/kinston', county: 'Lenoir', region: 'Eastern NC', type: 'city' },
  { name: 'Wilmington', path: '/locations/wilmington', county: 'New Hanover', region: 'Coastal', type: 'metropolitan' },
  { name: 'Jacksonville', path: '/locations/jacksonville', county: 'Onslow', region: 'Coastal', type: 'city' },
  { name: 'Elizabeth City', path: '/locations/elizabeth-city', county: 'Pasquotank', region: 'Coastal', type: 'city' },
  { name: 'Henderson', path: '/locations/henderson', county: 'Vance', region: 'Northern NC', type: 'city' },
  { name: 'Tarboro', path: '/locations/tarboro', county: 'Edgecombe', region: 'Eastern NC', type: 'city' },
  
  // Towns & Smaller Communities
  { name: 'Nashville', path: '/locations/nashville', county: 'Nash', region: 'Eastern NC', type: 'town' },
  { name: 'Bailey', path: '/locations/bailey', county: 'Nash', region: 'Eastern NC', type: 'town' },
  { name: 'Spring Hope', path: '/locations/spring-hope', county: 'Nash', region: 'Eastern NC', type: 'town' },
  { name: 'Red Oak', path: '/locations/red-oak', county: 'Nash', region: 'Eastern NC', type: 'town' },
  { name: 'Zebulon', path: '/locations/zebulon', county: 'Wake', region: 'Triangle', type: 'town' },
  { name: 'Wake Forest', path: '/locations/wake-forest', county: 'Wake', region: 'Triangle', type: 'town' },
  { name: 'Wendell', path: '/locations/wendell', county: 'Wake', region: 'Triangle', type: 'town' },
  { name: 'Knightdale', path: '/locations/knightdale', county: 'Wake', region: 'Triangle', type: 'town' },
  { name: 'Garner', path: '/locations/garner', county: 'Wake', region: 'Triangle', type: 'town' },
  { name: 'Fuquay-Varina', path: '/locations/fuquay-varina', county: 'Wake', region: 'Triangle', type: 'town' },
  { name: 'Clayton', path: '/locations/clayton', county: 'Johnston', region: 'Triangle', type: 'town' },
  { name: 'Smithfield', path: '/locations/smithfield', county: 'Johnston', region: 'Eastern NC', type: 'town' },
  { name: 'Selma', path: '/locations/selma', county: 'Johnston', region: 'Eastern NC', type: 'town' },
  { name: 'Kenly', path: '/locations/kenly', county: 'Johnston', region: 'Eastern NC', type: 'town' },
  { name: 'Princeton', path: '/locations/princeton', county: 'Johnston', region: 'Eastern NC', type: 'town' },
  { name: 'Four Oaks', path: '/locations/four-oaks', county: 'Johnston', region: 'Eastern NC', type: 'town' },
  { name: 'Pine Level', path: '/locations/pine-level', county: 'Johnston', region: 'Eastern NC', type: 'town' },
  { name: 'Micro', path: '/locations/micro', county: 'Johnston', region: 'Eastern NC', type: 'town' },
  { name: 'Middlesex', path: '/locations/middlesex', county: 'Nash', region: 'Eastern NC', type: 'town' },
  { name: 'Elm City', path: '/locations/elm-city', county: 'Wilson', region: 'Eastern NC', type: 'town' },
  { name: 'Lucama', path: '/locations/lucama', county: 'Wilson', region: 'Eastern NC', type: 'town' },
  { name: 'Black Creek', path: '/locations/black-creek', county: 'Wilson', region: 'Eastern NC', type: 'town' },
  { name: 'Sims', path: '/locations/sims', county: 'Wilson', region: 'Eastern NC', type: 'town' },
  { name: 'Stantonsburg', path: '/locations/stantonsburg', county: 'Wilson', region: 'Eastern NC', type: 'town' },
  
  // Coastal Communities
  { name: 'Nags Head', path: '/locations/nags-head', county: 'Dare', region: 'Outer Banks', type: 'coastal' },
  { name: 'Kill Devil Hills', path: '/locations/kill-devil-hills', county: 'Dare', region: 'Outer Banks', type: 'coastal' },
  { name: 'Kitty Hawk', path: '/locations/kitty-hawk', county: 'Dare', region: 'Outer Banks', type: 'coastal' },
  { name: 'Manteo', path: '/locations/manteo', county: 'Dare', region: 'Outer Banks', type: 'coastal' },
  { name: 'Hatteras', path: '/locations/hatteras', county: 'Dare', region: 'Outer Banks', type: 'coastal' },
  { name: 'Buxton', path: '/locations/buxton', county: 'Dare', region: 'Outer Banks', type: 'coastal' },
  { name: 'Avon', path: '/locations/avon', county: 'Dare', region: 'Outer Banks', type: 'coastal' },
  { name: 'Rodanthe', path: '/locations/rodanthe', county: 'Dare', region: 'Outer Banks', type: 'coastal' },
  { name: 'Waves', path: '/locations/waves', county: 'Dare', region: 'Outer Banks', type: 'coastal' },
  { name: 'Salvo', path: '/locations/salvo', county: 'Dare', region: 'Outer Banks', type: 'coastal' },
  { name: 'Ocracoke', path: '/locations/ocracoke', county: 'Hyde', region: 'Outer Banks', type: 'coastal' },
  { name: 'Morehead City', path: '/locations/morehead-city', county: 'Carteret', region: 'Crystal Coast', type: 'coastal' },
  { name: 'Beaufort', path: '/locations/beaufort', county: 'Carteret', region: 'Crystal Coast', type: 'coastal' },
  { name: 'Emerald Isle', path: '/locations/emerald-isle', county: 'Carteret', region: 'Crystal Coast', type: 'coastal' },
  { name: 'Atlantic Beach', path: '/locations/atlantic-beach', county: 'Carteret', region: 'Crystal Coast', type: 'coastal' },
  { name: 'Swansboro', path: '/locations/swansboro', county: 'Onslow', region: 'Crystal Coast', type: 'coastal' },
  
  // Additional Eastern NC Communities
  { name: 'Pinetops', path: '/locations/pinetops', county: 'Edgecombe', region: 'Eastern NC', type: 'town' },
  { name: 'Macclesfield', path: '/locations/macclesfield', county: 'Edgecombe', region: 'Eastern NC', type: 'town' },
  { name: 'Battleboro', path: '/locations/battleboro', county: 'Edgecombe', region: 'Eastern NC', type: 'town' },
  { name: 'Sharpsburg', path: '/locations/sharpsburg', county: 'Nash', region: 'Eastern NC', type: 'town' },
  { name: 'Castalia', path: '/locations/castalia', county: 'Nash', region: 'Eastern NC', type: 'town' },
  { name: 'Louisburg', path: '/locations/louisburg', county: 'Franklin', region: 'Northern NC', type: 'town' },
  { name: 'Franklinton', path: '/locations/franklinton', county: 'Franklin', region: 'Northern NC', type: 'town' },
  { name: 'Youngsville', path: '/locations/youngsville', county: 'Franklin', region: 'Northern NC', type: 'town' },
  { name: 'Bunn', path: '/locations/bunn', county: 'Franklin', region: 'Northern NC', type: 'town' }
];

const LocationsDirectory: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRegion, setSelectedRegion] = useState<string>('all');
  const [selectedType, setSelectedType] = useState<string>('all');

  const regions = ['Triangle', 'Eastern NC', 'Coastal', 'Outer Banks', 'Crystal Coast', 'Northern NC'];
  const types = ['metropolitan', 'city', 'town', 'coastal'];

  const filteredLocations = useMemo(() => {
    return allLocations.filter(location => {
      const matchesSearch = location.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           location.county.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesRegion = selectedRegion === 'all' || location.region === selectedRegion;
      const matchesType = selectedType === 'all' || location.type === selectedType;
      
      return matchesSearch && matchesRegion && matchesType;
    });
  }, [searchTerm, selectedRegion, selectedType]);

  const getLocationIcon = (type: string) => {
    switch (type) {
      case 'metropolitan':
        return <Building className="h-5 w-5" />;
      case 'coastal':
        return <Navigation className="h-5 w-5" />;
      default:
        return <MapPin className="h-5 w-5" />;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'metropolitan':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'city':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'coastal':
        return 'bg-cyan-100 text-cyan-800 border-cyan-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const locationsByRegion = useMemo(() => {
    const grouped = filteredLocations.reduce((acc, location) => {
      if (!acc[location.region]) {
        acc[location.region] = [];
      }
      acc[location.region].push(location);
      return acc;
    }, {} as Record<string, LocationData[]>);

    // Sort locations within each region alphabetically
    Object.keys(grouped).forEach(region => {
      grouped[region].sort((a, b) => a.name.localeCompare(b.name));
    });

    return grouped;
  }, [filteredLocations]);

  return (
    <div className="space-y-8">
      {/* Search and Filters */}
      <div className="bg-muted/30 p-6 rounded-lg">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search locations..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          
          <select
            value={selectedRegion}
            onChange={(e) => setSelectedRegion(e.target.value)}
            className="px-3 py-2 border border-input bg-background rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <option value="all">All Regions</option>
            {regions.map(region => (
              <option key={region} value={region}>{region}</option>
            ))}
          </select>

          <select
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
            className="px-3 py-2 border border-input bg-background rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <option value="all">All Types</option>
            {types.map(type => (
              <option key={type} value={type}>
                {type.charAt(0).toUpperCase() + type.slice(1)}
              </option>
            ))}
          </select>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <div className="flex items-center gap-1 text-sm text-muted-foreground">
            <Users className="h-4 w-4" />
            <span>{filteredLocations.length} locations found</span>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-primary">{allLocations.filter(l => l.type === 'metropolitan').length}</div>
            <div className="text-sm text-muted-foreground">Metropolitan Areas</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-primary">{allLocations.filter(l => l.type === 'city').length}</div>
            <div className="text-sm text-muted-foreground">Cities</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-primary">{allLocations.filter(l => l.type === 'town').length}</div>
            <div className="text-sm text-muted-foreground">Towns</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-primary">{allLocations.filter(l => l.type === 'coastal').length}</div>
            <div className="text-sm text-muted-foreground">Coastal Areas</div>
          </CardContent>
        </Card>
      </div>

      {/* Locations by Region */}
      <div className="space-y-8">
        {Object.entries(locationsByRegion).map(([region, locations]) => (
          <div key={region}>
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
              <MapPin className="h-6 w-6 text-primary" />
              {region}
              <Badge variant="secondary" className="ml-2">
                {locations.length} locations
              </Badge>
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {locations.map((location) => (
                <Card key={location.path} className="hover:shadow-md transition-shadow">
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center justify-between text-lg">
                      <div className="flex items-center gap-2">
                        {getLocationIcon(location.type)}
                        {location.name}
                      </div>
                      <Badge 
                        variant="outline" 
                        className={getTypeColor(location.type)}
                      >
                        {location.type}
                      </Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="text-sm text-muted-foreground">
                        {location.county} County • {location.region}
                      </div>
                      
                      <Link to={location.path}>
                        <Button variant="outline" size="sm" className="w-full">
                          View Location Details
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        ))}
      </div>

      {filteredLocations.length === 0 && (
        <Card>
          <CardContent className="p-8 text-center">
            <MapPin className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">No locations found</h3>
            <p className="text-muted-foreground">
              Try adjusting your search terms or filters to find the location you're looking for.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default LocationsDirectory;